// src/services/configService.js
import axios from 'axios';

const CONFIG_API_URLS = {
  dev: 'https://conversation-dev.agentdeveloper.agent-enterprise.com/config',
  prod: 'https://conversation.agentdeveloper.agent-enterprise.com/config'
};

class ConfigService {
  constructor() {
    this.config = null;
    this.loading = false;
    this.loadingPromise = null;
    this.error = null;
    this.environment = process.env.REACT_APP_STAGE || 'prod';
  }

  getConfigUrl() {
    return CONFIG_API_URLS[this.environment] || CONFIG_API_URLS.prod;
  }

  async fetchConfig() {
    // If config is already loaded, return it
    if (this.config) {
      console.log('✅ Config already loaded, returning cached config');
      return this.config;
    }

    // If currently loading, wait for the existing promise
    if (this.loadingPromise) {
      console.log('⏳ Config fetch in progress, waiting...');
      return this.loadingPromise;
    }

    // Start loading
    this.loadingPromise = this._doFetchConfig();
    
    try {
      const result = await this.loadingPromise;
      return result;
    } finally {
      this.loadingPromise = null;
    }
  }

  async _doFetchConfig() {
    this.loading = true;
    this.error = null;

    try {
      const configUrl = this.getConfigUrl();
      console.log('📡 Fetching config from:', configUrl);

      const response = await axios.get(configUrl, {
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('📥 Config response:', response.data);

      // Handle the response - check for nested body if it comes as string
      let configData = response.data;
      
      // If body is a string (from Lambda proxy integration), parse it
      if (typeof configData.body === 'string') {
        try {
          configData = JSON.parse(configData.body);
        } catch (e) {
          console.error('Failed to parse body as JSON:', e);
        }
      }

      if (configData && configData.success && configData.config) {
        this.config = configData.config;
        console.log('✅ Config loaded successfully:', this.config);
        console.log('🔌 WebSocket URL:', this.config.websocket?.url);
        
        // Cache config in localStorage with timestamp
        localStorage.setItem('apiConfig', JSON.stringify({
          config: this.config,
          timestamp: Date.now()
        }));

        return this.config;
      } else {
        throw new Error('Invalid config response structure');
      }
    } catch (error) {
      console.error('❌ Failed to fetch config:', error);
      this.error = error;

      // Try to load from cache
      const cached = localStorage.getItem('apiConfig');
      if (cached) {
        try {
          const { config, timestamp } = JSON.parse(cached);
          const age = Date.now() - timestamp;
          
          // Use cache if less than 24 hours old
          if (age < 24 * 60 * 60 * 1000 && config) {
            console.log('⚠️ Using cached config (age:', Math.round(age / 1000 / 60), 'minutes)');
            this.config = config;
            return config;
          }
        } catch (e) {
          console.error('Failed to parse cached config:', e);
        }
      }

      // Return fallback config
      console.warn('⚠️ Using fallback config');
      this.config = this.getFallbackConfig();
      return this.config;
    } finally {
      this.loading = false;
    }
  }

  getFallbackConfig() {
    return {
      websocket: {
        url: 'wss://ws.agent-enterprise.com',
        fallbackUrl: 'wss://2uajso5vr4.execute-api.us-west-2.amazonaws.com/prod',
        customDomain: true
      },
      api: {
        version: '1.0.0',
        environment: this.environment
      },
      features: {
        realTimeUpdates: true,
        taskExecution: true,
        conversationHistory: true
      }
    };
  }

  getWebSocketUrl() {
    if (!this.config) {
      console.warn('⚠️ Config not loaded yet, returning fallback WebSocket URL');
      return 'wss://ws.agent-enterprise.com';
    }
    
    const url = this.config.websocket?.url || this.config.websocket?.fallbackUrl;
    console.log('🔌 getWebSocketUrl returning:', url);
    return url;
  }

  getConfig() {
    return this.config;
  }

  isLoaded() {
    return this.config !== null;
  }

  clearCache() {
    this.config = null;
    this.loadingPromise = null;
    localStorage.removeItem('apiConfig');
  }
}

const configService = new ConfigService();
export default configService;
